import React from 'react'
import { AdvisorDistributionEntry } from '@/redux/types/usageStats'
import {
  CHART_COLORS,
  NO_MSG_COLOR,
  describeArc,
  getActivityColor,
} from '@/utils/advisorChartUtils'

interface AdvisorDonutChartProps {
  distribution: AdvisorDistributionEntry[]
  size?: number
  ringWidth?: number
  showLegend?: boolean
}

const AdvisorDonutChart: React.FC<AdvisorDonutChartProps> = ({
  distribution,
  size = 120,
  ringWidth = 20,
  showLegend = false,
}) => {
  const cx = size / 2
  const cy = size / 2
  const outerR = size / 2 - 4
  const innerR = outerR - ringWidth

  const total = distribution.reduce((sum, d) => sum + d.message_count, 0)
  const activityColor = getActivityColor(distribution, total)
  const indicatorR = innerR * 0.45

  const GAP = distribution.length > 1 ? 2 : 0

  const renderSlices = () => {
    if (total === 0 || distribution.length === 0) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={(outerR + innerR) / 2}
          fill='none'
          stroke={NO_MSG_COLOR}
          strokeWidth={ringWidth}
        />
      )
    }

    let currentAngle = 0
    return distribution.map((entry, i) => {
      const sliceAngle = (entry.message_count / total) * 360
      const startAngle = currentAngle + GAP / 2
      const endAngle = currentAngle + sliceAngle - GAP / 2
      currentAngle += sliceAngle

      if (endAngle <= startAngle) return null

      const d = describeArc(cx, cy, outerR, innerR, startAngle, endAngle)
      return (
        <path
          key={entry.chatbot_id}
          d={d}
          fill={CHART_COLORS[i % CHART_COLORS.length]}
        />
      )
    })
  }

  return (
    <div className='flex flex-col items-center gap-3'>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        overflow='visible'
      >
        {renderSlices()}
        {activityColor && (
          <circle cx={cx} cy={cy} r={indicatorR} fill={activityColor} />
        )}
      </svg>

      {showLegend && distribution.length > 0 && (
        <div className='flex flex-col gap-1.5 w-full'>
          {distribution.map((entry, i) => (
            <div key={entry.chatbot_id} className='flex items-center gap-2'>
              <span
                className='inline-block w-3 h-3 rounded-full flex-shrink-0'
                style={{
                  backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                }}
              />
              <span className='text-sm text-foreground'>
                {entry.chatbot_name ?? entry.chatbot_id}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AdvisorDonutChart
